import React, { useEffect, useState } from 'react';
import { describePeriod, formatGap, formatInt, formatMoneyCompact, formatPct, isNum, ordinal } from '../format';
import { breadcrumbItems } from '../head';
import { agentFacts, agentHeadline, agentThinNote } from '../narrative';
import { track } from '../../utils/track';
import useSeoDocumentTitle from '../useSeoDocumentTitle';
import GapTrendChart from '../components/GapTrendChart';
import HeroStat from '../components/HeroStat';
import { AgentRankList, AgentSuburbTable, ResultsTable } from '../components/Tables';
import { ClaimProfile, NearbySuburbs, SeoFooter } from '../components/Panels';
import { Breadcrumbs, FreshnessLine, MethodologyNote, Notice, Section, SegmentedControl, StatGrid } from '../components/Primitives';

const TYPE_OPTIONS = [
  { value: 'all', label: 'All sales' },
  { value: 'house', label: 'Houses' },
  { value: 'unit', label: 'Units' },
];

function statsItems(stats, payload) {
  const since = payload.active_since ? payload.active_since.slice(0, 4) : null;
  return [
    {
      label: 'Typical miss',
      value: isNum(stats.guide_error_abs_pct) ? formatPct(stats.guide_error_abs_pct) : null,
      note: 'either direction',
    },
    {
      label: 'Sales with a guide',
      value: formatInt(stats.guide_matched_sales),
      note: describePeriod(payload.data_period),
    },
    {
      label: 'Median sale price',
      value: isNum(stats.median_sale_price) ? formatMoneyCompact(stats.median_sale_price) : null,
    },
    {
      label: 'Median days on market',
      value: isNum(stats.median_days_on_market) ? formatInt(stats.median_days_on_market) : null,
    },
    {
      label: 'Sales recorded',
      value: isNum(stats.total_sales_all_time) ? formatInt(stats.total_sales_all_time) : null,
      note: since ? `since ${since}` : 'all time',
    },
  ];
}

export default function AgentPage({ payload }) {
  const [type, setType] = useState('all');
  useSeoDocumentTitle('agent', payload);
  useEffect(() => {
    track('agent_view', { slug: payload.slug, indexable: payload.indexable });
  }, [payload.slug, payload.indexable]);

  const hasBoth = Boolean(payload.by_type?.house && payload.by_type?.unit);
  const stats = type === 'all' ? payload.headline : payload.by_type?.[type] || payload.headline;
  const headline = agentHeadline(payload);
  const facts = agentFacts(payload);
  const thin = agentThinNote(payload);
  const rank = payload.rank || {};
  const mainSuburb = payload.suburbs?.[0];
  const suburbCards = (payload.suburbs || [])
    .filter((s) => s.has_page)
    .map((s) => ({ slug: s.slug, name: s.name, guide_gap_pct: s.suburb_guide_gap_pct, median_sale_price: null, indexable: true }));

  return (
    <main className="re-seo re-seo--agent" id="main">
      <div className="re-seo__inner">
        <Breadcrumbs items={breadcrumbItems('agent', payload)} />

        <header className="re-masthead">
          <p className="re-kicker">{`Agent price guide record${payload.agency?.name ? ` · ${payload.agency.name}` : ''}`}</p>
          <h1>{payload.name}</h1>
          <p className="re-lede">{headline.text}</p>
        </header>

        {thin ? <Notice>{thin}</Notice> : null}

        <section className="re-lead" aria-label="Headline figures">
          <div className="re-lead__primary">
            {hasBoth ? (
              <SegmentedControl label="Property type" options={TYPE_OPTIONS} value={type} onChange={setType} />
            ) : null}
            <HeroStat
              value={stats.guide_gap_pct}
              caption={`Average result against advertised guide, ${describePeriod(payload.data_period)}.`}
              footer={<FreshnessLine period={payload.data_period} count={stats.guide_matched_sales} />}
            />
          </div>
          <div className="re-lead__secondary">
            {isNum(rank.guide_error_abs_percentile) ? (
              <div className="re-percentile">
                <p className="re-percentile__figure">
                  <span>{Math.round(rank.guide_error_abs_percentile)}</span>
                  <small>th percentile</small>
                </p>
                <div
                  className="re-percentile__bar"
                  role="img"
                  aria-label={`Closer to guide than ${Math.round(rank.guide_error_abs_percentile)}% of ranked agents`}
                >
                  <span style={{ width: `${rank.guide_error_abs_percentile}%` }} />
                </div>
                <p className="re-percentile__caption">
                  Results closer to guide than {Math.round(rank.guide_error_abs_percentile)}% of ranked Sydney agents
                  {rank.guide_error_abs ? ` (${ordinal(rank.guide_error_abs.position)} of ${formatInt(rank.guide_error_abs.of)})` : ''}.
                </p>
              </div>
            ) : (
              <p className="re-muted re-percentile__none">Not ranked: too few sales with a published guide.</p>
            )}
            {mainSuburb && isNum(mainSuburb.suburb_guide_gap_pct) ? (
              <ul className="re-ranks">
                <li>
                  In {mainSuburb.name}: <strong>{formatGap(mainSuburb.guide_gap_pct)}</strong> against{' '}
                  <strong>{formatGap(mainSuburb.suburb_guide_gap_pct)}</strong> for the suburb overall
                </li>
              </ul>
            ) : null}
          </div>
        </section>

        <StatGrid items={statsItems(stats, payload)} />

        {facts.length ? (
          <Section id="notable" title="What stands out" kicker="From the numbers">
            <ul className="re-facts">
              {facts.map((f) => (
                <li key={f.text}>{f.text}</li>
              ))}
            </ul>
          </Section>
        ) : null}

        {payload.suburbs?.length ? (
          <Section id="suburbs" title="Where they sell" kicker="Suburbs">
            <AgentSuburbTable suburbs={payload.suburbs} agentName={payload.name} />
            <MethodologyNote anchor="gap">
              Their average gap in each suburb, beside the median gap for all sales there.
            </MethodologyNote>
          </Section>
        ) : null}

        {payload.trend?.length && !thin ? (
          <Section id="trend" title="Results against guide, month by month" kicker="Trend">
            <GapTrendChart
              trend={payload.trend}
              seriesLabel="Average gap each month"
              title={`${payload.name}: average gap between sale price and advertised guide, by month`}
              description="Above the guide line, sales finished above the advertised guide. Below it, under. The bars show how many sales each month is based on. Months with few sales swing more."
            />
          </Section>
        ) : null}

        <Section id="results" title="Recent results" kicker="Sold">
          <ResultsTable
            results={payload.recent_results}
            showAgent={false}
            showSuburb
            caption={`Recent sales by ${payload.name} with guide and result`}
            from="agent"
          />
        </Section>

        {payload.peers?.length ? (
          <Section id="peers" title={mainSuburb ? `Other agents in ${mainSuburb.name}` : 'Other agents nearby'} kicker="Compare">
            <AgentRankList agents={payload.peers} caption="Other agents active in the same suburbs" from="agent" />
          </Section>
        ) : null}

        {suburbCards.length ? (
          <Section id="suburb-links" title="Suburb profiles" kicker="Explore">
            <NearbySuburbs items={suburbCards} from={payload.slug} />
          </Section>
        ) : null}

        <ClaimProfile name={payload.name} slug={payload.slug} />

        <MethodologyNote anchor="error">
          These figures measure how sale prices compared with advertised guides. A result above guide can reflect strong
          competition on the day as much as the guide itself.
        </MethodologyNote>

        <SeoFooter dataVersion={payload.data_version} />
      </div>
    </main>
  );
}
